import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "482",
  "slug": "what-is-a-sand-dune-how-dunes-form",
  "title": "What Is a Sand Dune? How Dunes Form and Why They Move",
  "excerpt": "Sand dunes are some of the most recognisable shapes on Earth from above. Here is how wind builds them, why they have a gentle and a steep side, and how they slowly march across the land.",
  "date": "2026-06-19",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1617374128851-c84e37dc9f37?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYW5kJTIwZHVuZXMlMjBkZXNlcnR8ZW58MXwwfHx8MTc4MTg1Njc4NXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A sand dune is a mound or ridge of wind-blown sand. Dunes form wherever there is a supply of loose sand, wind strong enough to move it, and something to slow that wind down so the sand drops. They migrate downwind as grains blow up the gentle slope and avalanche down the steep face.",
  "content": [
    {
      "type": "paragraph",
      "text": "From the window of a plane or in a satellite image, a sea of dunes looks almost alive: long ridges, crescents and stars rippling across the desert in repeating patterns. Those patterns are not random. Each dune shape is a record of the wind that built it. Once you can read them, a stretch of empty desert turns into one of the most informative landscapes on the planet."
    },
    {
      "type": "heading",
      "text": "What Is a Sand Dune?"
    },
    {
      "type": "paragraph",
      "text": "A sand dune is simply a mound or ridge of loose sand piled up by the wind. Dunes are not limited to hot deserts. They form along coastlines, in cold polar deserts, on river banks, and even on Mars. What they all share is a recipe of three ingredients: a steady supply of sand-sized grains, wind strong enough to lift and roll those grains, and some change in the surface that makes the wind slow down and drop its load."
    },
    {
      "type": "heading",
      "text": "How Dunes Form: Sand, Wind, and an Obstacle"
    },
    {
      "type": "paragraph",
      "text": "Wind moves sand mostly by a process called saltation. Grains do not float through the air like dust; they hop. The wind lifts a grain a short distance, it falls back down, and the impact kicks several more grains into the air. A whole layer of sand can be skipping downwind a few centimetres above the surface, like a low, hissing carpet."
    },
    {
      "type": "paragraph",
      "text": "When that moving sand reaches a spot where the wind slows down — a rock, a clump of grass, a dip in the ground, or even an existing patch of sand — the air can no longer carry as much, so grains settle out. That small pile slows the wind a little more, which traps more sand, which grows the pile further. This feedback is why dunes tend to grow once they start, and why a single tuft of beach grass can be the seed of a dune metres high."
    },
    {
      "type": "heading",
      "text": "Why Dunes Have a Gentle Side and a Steep Side"
    },
    {
      "type": "paragraph",
      "text": "Most dunes are lopsided, and that asymmetry is the key to reading them. The side facing the wind, the windward slope, is long and gentle because the wind is constantly pushing sand up it. The far side, the slip face, is short and steep. Sand blows up and over the crest, piles up just past the top, and eventually avalanches down the sheltered side. That sliding sand settles at the steepest angle loose grains can hold without collapsing — roughly 30 to 34 degrees, known as the angle of repose. So the steep slip face always points downwind, which means a dune is essentially an arrow showing you which way the wind blows."
    },
    {
      "type": "heading",
      "text": "The Main Types of Dunes"
    },
    {
      "type": "paragraph",
      "text": "Geographers group dunes by their shape, and each shape reflects how much sand is available and how many directions the wind comes from:"
    },
    {
      "type": "list",
      "items": [
        "Barchan (crescent) dunes: isolated, crescent-shaped dunes with their horns pointing downwind. They form where sand is limited and the wind blows mostly one way.",
        "Transverse dunes: long ridges lying across the wind, like ocean waves frozen in place. They need a plentiful sand supply and steady wind.",
        "Linear (longitudinal) dunes: long, roughly parallel ridges that run in line with the average wind, often formed by winds from two directions.",
        "Star dunes: tall pyramids with arms radiating from a central peak, built where the wind blows from many directions through the year. These are among the tallest dunes on Earth.",
        "Parabolic dunes: U-shaped dunes with horns pointing upwind, anchored by vegetation. Common along coasts, they are the mirror image of barchans."
      ]
    },
    {
      "type": "heading",
      "text": "Why Dunes Move"
    },
    {
      "type": "paragraph",
      "text": "A dune is not a fixed hill — it is a process caught in slow motion. Because sand is endlessly carried up the gentle slope and dropped over the crest onto the slip face, the whole dune creeps downwind over time. Small desert dunes can advance several metres a year; large ones move much more slowly. This is why moving dunes can bury roads, farmland and even villages, and why stabilising them with vegetation is a constant battle on the edges of deserts."
    }
  ]
};
